import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "../branding/Logo";
import { LanguageSwitcher } from "../LanguageSwitcher";

interface LegalPageLayoutProps {
    title: string;
    children: React.ReactNode;
}

export function LegalPageLayout({ title, children }: LegalPageLayoutProps) {
    const { t, i18n } = useTranslation();
    const isEnglish = i18n.language.startsWith("en");

    return (
        <div className="min-h-screen bg-slate-50 font-nunito">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                         <Logo size="md" variant="image" />
                         <div className="flex items-center">
                              <span className="text-lg font-bold" style={{ color: '#237CCA' }}>Eq</span>
                              <span className="text-lg font-bold" style={{ color: '#10B981' }}>una</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher />
                        <Link
                            to="/"
                            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-semibold text-sm transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {t("legal.back")}
                        </Link>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="container mx-auto px-4 py-12 md:py-16">
                <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
                    <h1 className="text-3xl font-bold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                        {title}
                    </h1>

                    {/* Unofficial translation notice when viewing in English */}
                    {isEnglish && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-sm text-amber-900">
                            <p className="font-semibold mb-1">Unofficial translation</p>
                            <p className="leading-relaxed">
                                This English version is provided for convenience. Only the{" "}
                                <a href="?lang=de" onClick={(e) => { e.preventDefault(); i18n.changeLanguage("de"); }} className="underline font-medium">
                                    German version
                                </a>{" "}
                                is legally binding. In case of any discrepancy, the German original prevails.
                            </p>
                        </div>
                    )}

                    <div className="prose prose-slate max-w-none text-slate-600">
                        {children}
                    </div>
                </div>
            </main>

             {/* Footer - Minimal */}
             <footer className="py-8 text-center text-slate-400 text-sm">
                <p>&copy; {new Date().getFullYear()} Equna. {t("legal.footerRights")}</p>
                <p className="mt-2">
                    <Link to="/datenschutz" className="hover:text-slate-600 transition-colors">{t("legal.nav.privacy")}</Link>
                    {" · "}
                    <Link to="/agb" className="hover:text-slate-600 transition-colors">{t("legal.nav.terms")}</Link>
                    {" · "}
                    <Link to="/impressum" className="hover:text-slate-600 transition-colors">{t("legal.nav.imprint")}</Link>
                    {" · "}
                    <Link to="/support" className="hover:text-slate-600 transition-colors">{t("legal.nav.support")}</Link>
                </p>
            </footer>
        </div>
    );
}
